import {
  createInventorizationIssue,
  INVENTORIZATION_BLOCK_IDS,
} from '@/features/inventorization/domain/inventorization';
import type { InventorizationRegisterReading } from '@/features/inventorization/domain/inventorization-reading-selectors';
import {
  DEFAULT_INVENTORIZATION_REGISTER_ID,
  isDefaultInventorizationRegisterLabel,
} from '@/features/inventorization/domain/inventorization-registers';
import { type CaptureSession, PHOTO_CONTEXTS, type ValidationIssue } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export function validateInventorizationRegisterReadings(
  session: CaptureSession,
  registers: InventorizationRegisterReading[],
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const blockId = INVENTORIZATION_BLOCK_IDS.readings;

  registers.forEach((reading, index) => {
    const values = { register: readRegisterDisplayLabel(reading.label, index) };

    if (!reading.counter.trim()) {
      issues.push(
        createInventorizationIssue(
          blockId,
          `READING_${reading.id}_COUNTER_MISSING`,
          translateInventoryText(
            'app.inventory.validation.register.counterRequired',
            '{register}: counter is required.',
            values,
          ),
        ),
      );
    }

    if (!reading.unit.trim()) {
      issues.push(
        createInventorizationIssue(
          blockId,
          `READING_${reading.id}_UNIT_MISSING`,
          translateInventoryText(
            'app.inventory.validation.register.unitRequired',
            '{register}: unit is required.',
            values,
          ),
        ),
      );
    }

    if (!reading.obis.trim()) {
      issues.push(
        createInventorizationIssue(
          blockId,
          `READING_${reading.id}_OBIS_MISSING`,
          translateInventoryText(
            'app.inventory.validation.register.obisRequired',
            '{register}: OBIS/tariff code is required.',
            values,
          ),
        ),
      );
    }

    if (!hasRegisterPhoto(session, reading.photoRef || reading.id)) {
      issues.push(
        createInventorizationIssue(
          blockId,
          `READING_${reading.id}_REGISTER_PHOTO_MISSING`,
          translateInventoryText(
            'app.inventory.validation.register.photoRequired',
            '{register}: counter photo is required.',
            values,
          ),
        ),
      );
    }
  });

  return issues;
}

function readRegisterDisplayLabel(label: string, index: number) {
  return label && !isDefaultInventorizationRegisterLabel(label, index)
    ? label
    : translateInventoryText('app.inventory.review.register.label', 'Register {number}', { number: index + 1 });
}

function hasRegisterPhoto(session: CaptureSession, registerId: string) {
  return session.photos.some(
    photo =>
      photo.context === PHOTO_CONTEXTS.register &&
      (photo.registerId === registerId || (!photo.registerId && registerId === DEFAULT_INVENTORIZATION_REGISTER_ID)),
  );
}
